import React, { useEffect, useState } from "react";
import { DialogWrapMedium } from "../../../controls/components/dialogWrap/DialogWrap";
import { AdminFilterStoreControl } from "./FilterPartner";
import { InputOrderAdminTitle, UserFormServiceButtonClose } from "../styled/DesStyled";
import { partnerDetail } from "../../../../services/LocationService";
import { createOrderPrefillLink, estimateFee, getOrderForPrefill } from "../../../../services/OrderService";
import { getPublicSiteOrigin } from "../../../../utils/stationCheckoutLink";
import { toOrderCalendarDateInput } from "../../../../utils/orderCalendarDate";

// "Payment link" (Orders): Admin điền trạm + số túi + ngày (được là ngày đã qua) + liên hệ khách, hoặc nạp từ một
// booking cũ. Máy chủ mã hoá thành link /order?p=<mã> (liên hệ khách không nằm trần trên URL). Khách mở link,
// xem lại rồi tự chọn cách trả; đơn được tạo lúc khách trả, đi đúng luồng thường (email, PIN, Reserved, báo trạm).
// Hộp này KHÔNG tạo đơn, không chạm tiền.

const inputStyle: React.CSSProperties = {
	width: "100%",
	height: 40,
	padding: "0 10px",
	border: "1px solid #d1d5db",
	borderRadius: 6,
	fontSize: 14,
	boxSizing: "border-box",
};
const btn: React.CSSProperties = { height: 40, padding: "0 14px", borderRadius: 6, border: "1px solid #d1d5db", background: "#f3f4f6", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" };
const row2: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };

function addDays(ymd: string, n: number): string {
	const [y, m, d] = ymd.split("-").map(Number);
	return toOrderCalendarDateInput(new Date(y, m - 1, d + n));
}

export default function PaymentLinkDialog(props: { openDialog: boolean; setOpenDialog: (v: boolean) => void }) {
	const today = toOrderCalendarDateInput(new Date());
	const [sourceId, setSourceId] = useState("");
	const [source, setSource] = useState<{ id: number; pickUp: string } | null>(null);
	const [partnerId, setPartnerId] = useState<number | string>("");
	const [partnerLabel, setPartnerLabel] = useState("");
	const [quantity, setQuantity] = useState("1");
	const [dropOff, setDropOff] = useState(today);
	const [pickUp, setPickUp] = useState(today);
	const [fullName, setFullName] = useState("");
	const [email, setEmail] = useState("");
	const [phonePrefix, setPhonePrefix] = useState("");
	const [phone, setPhone] = useState("");
	// null = đang hỏi giá; "unavailable" = trạm không trả được giá (đã tắt / ngừng nhận đơn / mất mạng).
	const [price, setPrice] = useState<{ days: number; total: number } | "unavailable" | null>(null);
	const [url, setUrl] = useState("");
	const [note, setNote] = useState("");
	const [busy, setBusy] = useState(false);
	const [copied, setCopied] = useState(false);

	const qty = Number(quantity);
	const error = !partnerId
		? "Choose a station."
		: !Number.isInteger(qty) || qty < 1 || qty > 50
			? "Bags must be a whole number from 1 to 50."
			: !dropOff || !pickUp
				? "Choose both dates."
				: pickUp < dropOff
					? "Pick-up date must be on or after the drop-off date."
					: "";

	// Đổi bất cứ thứ gì thì link cũ không còn khớp: bỏ, bắt bấm tạo lại.
	useEffect(() => setUrl(""), [partnerId, quantity, dropOff, pickUp, fullName, email, phonePrefix, phone]);

	// Giá xem trước: đúng công thức máy chủ dùng khi khách trả.
	useEffect(() => {
		setPrice(null);
		if (error) return;
		let alive = true;
		(async () => {
			try {
				const p = await partnerDetail(Number(partnerId));
				const r = await estimateFee(Number(p.fee) || 0, qty, dropOff, pickUp, p.insuranceFee, p.timezoneId || "");
				if (alive) setPrice(r ? { days: Number(r.distanceDay) || 0, total: Number(r.estimateFee) || 0 } : "unavailable");
			} catch {
				if (alive) setPrice("unavailable");
			}
		})();
		return () => {
			alive = false;
		};
	}, [partnerId, qty, dropOff, pickUp, error]);

	const loadSource = async () => {
		const id = Number(sourceId.replace(/[^\d]/g, ""));
		if (!id) return setNote("Enter a booking ID.");
		setNote("");
		try {
			const o = await getOrderForPrefill(id);
			if (!o.partnerId) return setNote(`Booking #${id} not found.`);
			setSource({ id: o.id, pickUp: o.pickUp });
			setPartnerId(o.partnerId);
			setPartnerLabel(o.partnerLabel);
			setQuantity(String(o.quantity));
			setFullName(o.fullName);
			setEmail(o.email);
			setPhonePrefix(o.phonePrefix);
			setPhone(o.phone);
			setNote(`Loaded booking #${o.id} (${o.dropOff} → ${o.pickUp}). Set the dates for the new payment.`);
		} catch {
			setNote(`Could not load booking #${id}.`);
		}
	};

	// Khách lấy đồ trễ: trả từ ngày SAU ngày lấy cũ tới hôm nay (giá tính cả hai đầu, ngày lấy cũ đã trả).
	const extendFromSource = () => {
		if (!source?.pickUp) return;
		const from = addDays(source.pickUp, 1);
		setDropOff(from);
		setPickUp(from > today ? from : today);
	};

	const create = async () => {
		if (error) return;
		setBusy(true);
		setNote("");
		try {
			const r = await createOrderPrefillLink({
				partnerId: Number(partnerId),
				quantity: qty,
				dropOff,
				pickUp,
				fullName,
				email,
				phone,
				phonePrefix,
				sourceOrderId: source?.id ?? null,
			});
			setUrl(`${getPublicSiteOrigin()}/order?p=${encodeURIComponent(r.token)}`);
			setNote(`Link valid until ${new Date(r.expiresAt).toLocaleString("en-GB")}.`);
		} catch (e: any) {
			setNote(e?.response?.data?.message || "Could not create the link.");
		} finally {
			setBusy(false);
		}
	};

	const copy = () =>
		url &&
		navigator.clipboard.writeText(url).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		});

	return (
		<DialogWrapMedium
			open={props.openDialog}
			onClose={() => props.setOpenDialog(false)}
			title="Payment link for a customer"
			actions={
				<div style={{ display: "flex", justifyContent: "flex-end", padding: "0 4px 4px" }}>
					<UserFormServiceButtonClose type="button" onClick={() => props.setOpenDialog(false)}>
						Close
					</UserFormServiceButtonClose>
				</div>
			}>
			<div style={{ display: "grid", gap: 12, padding: "4px 4px 8px" }}>
				<div style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.5 }}>
					The customer opens the link, checks the booking and pays with any method. The order is created when they pay. Past
					dates are allowed. Links expire after 7 days.
				</div>
				<div>
					<InputOrderAdminTitle>Previous booking ID (optional)</InputOrderAdminTitle>
					<div style={{ display: "flex", gap: 6 }}>
						<input style={inputStyle} placeholder="e.g. 35413" value={sourceId} onChange={(e) => setSourceId(e.target.value)} />
						<button type="button" style={btn} onClick={loadSource}>
							Load
						</button>
						{source ? (
							<button type="button" style={btn} onClick={extendFromSource}>
								Extend from #{source.id}
							</button>
						) : null}
					</div>
				</div>
				<div>
					<InputOrderAdminTitle>Station</InputOrderAdminTitle>
					<AdminFilterStoreControl filterStaffId={partnerId} selectedLabel={partnerLabel} doChangeStaffId={(v: any) => setPartnerId(v || "")} />
				</div>
				<div style={row2}>
					<div>
						<InputOrderAdminTitle>Drop-off date</InputOrderAdminTitle>
						<input style={inputStyle} type="date" value={dropOff} onChange={(e) => setDropOff(e.target.value)} />
					</div>
					<div>
						<InputOrderAdminTitle>Pick-up date</InputOrderAdminTitle>
						<input style={inputStyle} type="date" value={pickUp} min={dropOff} onChange={(e) => setPickUp(e.target.value)} />
					</div>
				</div>
				<div style={row2}>
					<div>
						<InputOrderAdminTitle>Bags</InputOrderAdminTitle>
						<input style={inputStyle} type="number" min={1} max={50} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
					</div>
					<div>
						<InputOrderAdminTitle>Customer name</InputOrderAdminTitle>
						<input style={inputStyle} value={fullName} onChange={(e) => setFullName(e.target.value)} />
					</div>
				</div>
				<div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr", gap: 12 }}>
					<div>
						<InputOrderAdminTitle>Email</InputOrderAdminTitle>
						<input style={inputStyle} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
					</div>
					<div>
						<InputOrderAdminTitle>Code</InputOrderAdminTitle>
						<input style={inputStyle} placeholder="+84" value={phonePrefix} onChange={(e) => setPhonePrefix(e.target.value)} />
					</div>
					<div>
						<InputOrderAdminTitle>Phone</InputOrderAdminTitle>
						<input style={inputStyle} value={phone} onChange={(e) => setPhone(e.target.value)} />
					</div>
				</div>
				<div style={{ fontSize: 14, color: error || price === "unavailable" ? "#b42318" : "#111827" }}>
					{error ||
						(price === "unavailable"
							? "Price unavailable: this station may not be taking bookings."
							: price
								? `Customer pays: ${price.total.toFixed(2)} USD (${qty} ${qty === 1 ? "bag" : "bags"} × ${price.days} ${price.days === 1 ? "day" : "days"})`
								: "Checking price…")}
				</div>
				<button type="button" style={{ ...btn, background: "#035d9d", color: "#fff", borderColor: "#035d9d", opacity: error || busy ? 0.5 : 1 }} disabled={!!error || busy} onClick={create}>
					{busy ? "Creating…" : "Create link"}
				</button>
				{url ? (
					<div style={{ display: "flex", gap: 6 }}>
						<input readOnly value={url} aria-label="Payment link" onFocus={(e) => e.target.select()} style={{ ...inputStyle, flex: 1, minWidth: 0, fontSize: 12, fontFamily: "Consolas, monospace", background: "#f9fafb" }} />
						<button type="button" style={{ ...btn, background: copied ? "#e8f5e9" : "#f3f4f6" }} onClick={copy}>
							{copied ? "Copied ✓" : "Copy"}
						</button>
						<a href={url} target="_blank" rel="noopener noreferrer" title="Open in new tab" style={{ ...btn, display: "inline-flex", alignItems: "center", color: "#1565c0", background: "#e3f2fd", borderColor: "#90caf9", textDecoration: "none" }}>
							↗
						</a>
					</div>
				) : null}
				{note ? <div style={{ fontSize: 13, color: "#4b5563" }}>{note}</div> : null}
			</div>
		</DialogWrapMedium>
	);
}
